import moment from 'moment';
import i18next from 'i18next';

export const utcToLocal = (utcDate, format) => {
  return moment.utc(utcDate).local().format(format);
};

export const PERMISSION_TYPES = Object.freeze({
  ROLE: 'role',
  PERMISSION: 'permission',
});

export const PERMISSIONS_LEVEL = Object.freeze({
  ORGANIZATION: 'organization',
  FACILITY: 'facility',
});

export const REFUND_TYPES = {
  REGULAR: 'regular',
  INSURANCE: 'insurance',
  BUSINESS: 'business',
};

export const ENTITY_TYPE = Object.freeze({
  INDIVIDUAL: 'Individual',
  BUSINESS: 'Business',
  UNKNOWN: 'Unknown',
});

export const REFUND_METHOD = Object.freeze({
  ACH: 7,
  CHECK: 5,
  GIFTCARD: 4,
  DONATION: 6,
  PAYPAL: 2,
  VENMO: 3,
  TRANSFER_BACK: 8,
  CANCEL: 9,
  INSTANT_PAYMENT: 10,
  ZELLE: 11,
});

export const REFUND_METHOD_LABELS = Object.freeze({
  [REFUND_METHOD.PAYPAL]: 'Paypal',
  [REFUND_METHOD.VENMO]: 'Venmo',
  [REFUND_METHOD.GIFTCARD]: 'Gift Card',
  [REFUND_METHOD.CHECK]: 'Check',
  [REFUND_METHOD.DONATION]: 'Donations',
  [REFUND_METHOD.ACH]: 'ACH',
  [REFUND_METHOD.INSTANT_PAYMENT]: 'RTP',
  [REFUND_METHOD.ZELLE]: 'Zelle',
  0: 'Escheated',
});

export const CHECK_TYPE_DB = Object.freeze({
  ECHECK: 'echeck',
  PHYSICAL: 'physical',
});

export const TRANSACTION_STATUSES = Object.freeze({
  PENDING: 'PENDING',
  LEGACY_PENDING: 'Pending',
  SENT_UNCASHED: 'Sent-uncashed',
  ACH_SENT: 'ACH-sent',
  SUCCESS: 'Success',
  RETURNED: 'RETURNED',
  RETURNED_PENDING_RETURN_TO_BANK: 'RETURNED_PENDING_RETURN_TO_BANK',
  ACH_RETURNED: 'ACH-returned',
  VOIDED: 'Voided',
  ERROR: 'Error',
  ACH_RETURN_PENDING_RETURN_TO_BANK: 'ach_return_pending_return_to_bank',
  RETURNED_AVAILABLE_TO_RECLAIM: 'RETURNED_AVAILABLE_TO_RECLAIM',
  ACH_RETURN_AVAILABLE_TO_RECLAIM: 'ach_return_available_to_reclaim',
  STARTED: 'STARTED',
  LEGACY_STARTED: 'Started',
});

export const isStartedTransactionStatus = (status) => {
  const normalizedStatus = status?.toLowerCase();

  return (
    normalizedStatus === TRANSACTION_STATUSES.STARTED.toLowerCase() ||
    normalizedStatus === TRANSACTION_STATUSES.LEGACY_STARTED.toLowerCase()
  );
};

export const isPendingTransactionStatus = (status) => {
  const normalizedStatus = status?.toLowerCase();

  return (
    normalizedStatus === TRANSACTION_STATUSES.PENDING.toLowerCase() ||
    normalizedStatus === TRANSACTION_STATUSES.LEGACY_PENDING.toLowerCase()
  );
};

export const isReturnedTransactionStatus = (status) => {
  const normalizedStatus = status?.toLowerCase();

  return (
    normalizedStatus === TRANSACTION_STATUSES.RETURNED.toLowerCase() ||
    normalizedStatus === TRANSACTION_STATUSES.ACH_RETURNED.toLowerCase()
  );
};

export const isReturnedPendingReturnToBankTransactionStatus = (status) => {
  const normalizedStatus = status?.toLowerCase();

  return (
    normalizedStatus ===
      TRANSACTION_STATUSES.RETURNED_PENDING_RETURN_TO_BANK.toLowerCase() ||
    normalizedStatus ===
      TRANSACTION_STATUSES.ACH_RETURN_PENDING_RETURN_TO_BANK.toLowerCase()
  );
};

export const isReturnedAvailableToReclaimTransactionStatus = (status) => {
  const normalizedStatus = status?.toLowerCase();

  return (
    normalizedStatus ===
      TRANSACTION_STATUSES.RETURNED_AVAILABLE_TO_RECLAIM.toLowerCase() ||
    normalizedStatus ===
      TRANSACTION_STATUSES.ACH_RETURN_AVAILABLE_TO_RECLAIM.toLowerCase()
  );
};

export const isClaimableReturnedTransactionStatus = (status) => {
  return (
    isReturnedTransactionStatus(status) ||
    isReturnedAvailableToReclaimTransactionStatus(status)
  );
};

export const translateTransactionStatus = (status, t) => {
  if (!status || typeof status !== 'string') return '';

  if (isPendingTransactionStatus(status)) {
    return t('payments.transactionStatus.pending');
  }

  if (isStartedTransactionStatus(status)) {
    return t('payments.transactionStatus.started');
  }

  const normalizedStatus = status.toLowerCase();

  switch (normalizedStatus) {
    case TRANSACTION_STATUSES.SENT_UNCASHED.toLowerCase():
      return t('payments.transactionStatus.sentUncashed');
    case TRANSACTION_STATUSES.ACH_SENT.toLowerCase():
      return t('payments.transactionStatus.achSent');
    case TRANSACTION_STATUSES.SUCCESS.toLowerCase():
      return t('payments.transactionStatus.success');
    case TRANSACTION_STATUSES.RETURNED.toLowerCase():
    case TRANSACTION_STATUSES.ACH_RETURNED.toLowerCase():
      return t('payments.transactionStatus.returned');
    case TRANSACTION_STATUSES.VOIDED.toLowerCase():
      return t('payments.transactionStatus.voided');
    case TRANSACTION_STATUSES.ERROR.toLowerCase():
      return t('payments.transactionStatus.error');
    case TRANSACTION_STATUSES.RETURNED_PENDING_RETURN_TO_BANK.toLowerCase():
    case TRANSACTION_STATUSES.ACH_RETURN_PENDING_RETURN_TO_BANK.toLowerCase():
      return t('payments.transactionStatus.returnedPendingReturnToBank');
    case TRANSACTION_STATUSES.RETURNED_AVAILABLE_TO_RECLAIM.toLowerCase():
    case TRANSACTION_STATUSES.ACH_RETURN_AVAILABLE_TO_RECLAIM.toLowerCase():
      return t('payments.transactionStatus.returnedAvailableToReclaim');
    default:
      return status;
  }
};

export const SYSTEM_TAGS = Object.freeze({
  MANUAL_ENTRY: 'manual-entry',
  REFUND_FILE: 'refund-file',
});

export const APPROVAL_STATUS = Object.freeze({
  WAITING_FOR_APPROVAL: 'WAITING_FOR_APPROVAL',
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED',
});

export const REFUND_STATUSES = Object.freeze({
  UNCLAIMED: 'Unclaimed',
  REQUESTED: 'Requested',
  COMPLETED: 'Completed',
  CANCELED: 'Canceled',
  ESCHEATED: 'Escheated',
  EXCEPTION: 'Exception',
});

export const REFUND_SUB_STATUS = Object.freeze({
  EMAIL_SENT: 'Email Sent',
  TEXT_SENT: 'Text Sent',
  MAIL_SENT: 'Mail Sent',
  COMMUNICATION_UNDELIVERABLE: 'Communication Undeliverable',
  COMMUNICATION: 'Communication',
  COMMUNICATION_PENDING: 'Communication Pending',
  ESCHEATMENT_EMAIL_SENT: 'Escheatment Email Sent',
  ESCHEATMENT_PHYSICAL_MAIL_SENT: 'Escheatment Physical Mail Sent',
  ESCHEATMENT_CERTIFIED_MAIL_SENT: 'Escheatment Certified Mail Sent',
  ESCHEATMENT_NUAPA_CLIENT_REVIEW: 'Escheatment NUAPA Client Review',
  ESCHEATED_TO_STATE: 'Escheated to State',
  DORMANT_PENDING_ESCHEATMENT: 'Dormant Pending Escheatment',
  PAYEE_EXCEPTION: 'Payee Exception',
  ADDENDA_MISSING: 'Addenda Missing',
  PROCESSING_EXCEPTION: 'Processing Exception',
  TRANSFER_PENDING: 'Transfer Pending',
  TRANSFER_COMPLETE: 'Transfer Complete',
  TRANSFER_FAILED: 'Transfer Failed',
});
export const REFUND_FILE_TYPES = Object.freeze({
  PAYEE_FILE_ATTACHMENT: 'payee-file-attachment',
  INTERNAL_FILE_ATTACHMENT: 'internal-file-attachment',
});

export const REFUND_MODAL_TYPE = Object.freeze({
  CREATE: 'createPaymentForm',
  APPROVALS: 'approvalsPage',
  RESOLUTION_CENTER: 'resolutionCenter',
  REFUNDS_PAGE: 'refundsPage',
});

export const REFUND_PATH = Object.freeze({
  APPROVALS: 'approvals',
  TRANSACTIONS: 'transactions',
  PROCESSING_EXCEPTIONS: 'processing-exceptions',
  COMMUNICATION_EXCEPTIONS: 'communication-exceptions',
});

export const PAYMENT_METHODS = [
  { text: 'ACH', value: 7 },
  { text: 'Donation', value: 6 },
  { text: 'Check', value: 5 },
  { text: 'Gift Card', value: 4 },
  { text: 'PayPal', value: 2 },
  { text: 'Venmo', value: 3 },
  { text: 'RTP', value: 10 },
  { text: 'Zelle', value: 11 },
];

export const ORGANIZATION_FEATURES = {
  showBusinessUnitsPage: 'showBusinessUnitsPage',
  showApprovalsPage: 'showApprovalsPage',
  showCreatePaymentPage: 'showCreatePaymentPage',
  showFacilityManagementPage: 'showFacilityManagementPage',
  showSupplierLookupModal: 'showSupplierLookupModal',
  showBulkApproveDecline: 'showBulkApproveDecline',
};

export const SSO_FEATURE_FLAG = 'sso';

export const openPdf = (url) => {
  window.open(url, '_blank');
};

export const checkOrganizationFeatures = (key) => {
  const organizationFeatures = JSON.parse(
    localStorage.getItem('organizationFeatures') || '{}',
  );
  return !!organizationFeatures?.[key];
};

export const isPathOpen = (path) => {
  return (
    path.includes('user/login') ||
    path.includes('user/facilitylogin') ||
    path.includes('user/activation')
  );
};

export default function isAllowedForFacility({
  parentKey,
  childKey = 'viewPage',
  permission = 'read',
  facilityId,
}) {
  const appState = JSON.parse(localStorage.getItem('appState'));
  const role = appState?.user?.role;
  const permissionsType = appState?.user?.permissionsType;
  const hasAllFacilitiesAccess = appState?.user?.hasAllFacilitiesAccess;

  if (role === 'admin') {
    return true;
  }

  if (
    facilityId === null ||
    facilityId === undefined ||
    facilityId === '' ||
    !parentKey ||
    !childKey ||
    !permission
  ) {
    return false;
  }

  const userPermissions = JSON.parse(localStorage.getItem('userPermissions'));

  if (!userPermissions) {
    window.location.href = '/user/facilitylogin';
    return;
  }
  const matchingPermission =
    permissionsType === 'role'
      ? userPermissions?.find((p) => p.facility_id !== null)
      : hasAllFacilitiesAccess
      ? userPermissions?.find(
          (p) => p.facility_id === null && p.organization_id === null,
        )
      : userPermissions?.find((p) => p.facility_id === Number(facilityId));

  const userFacilityPermissions =
    matchingPermission?.permissions?.[parentKey]?.[childKey] ?? [];

  return userFacilityPermissions.includes(permission);
}

export const getPaymentTypeTranslation = (refundType) => {
  switch (refundType) {
    case REFUND_TYPES.REGULAR:
      return i18next.t('approvals.paymentTypes.consumer');
    case REFUND_TYPES.BUSINESS:
      return i18next.t('approvals.paymentTypes.business');
    case REFUND_TYPES.INSURANCE:
      return i18next.t('approvals.paymentTypes.insurance');
    default:
      return refundType;
  }
};
